import { createContext, useEffect, useRef, useState } from "react";
import { defaultSettings } from "../config";
import getColorPresets, {
  defaultPreset,
  colorPresets,
} from "../utils/getColorPresets";
import { getUserSettings, updateUserSettings } from "../apiservices";
import { useSocket } from "./SocketContext";

const initialState = {
  ...defaultSettings,

  // Mode
  onToggleMode: () => { },
  onChangeMode: () => { },

  // Direction
  onToggleDirection: () => { },
  onChangeDirection: () => { },
  onChangeDirectionByLang: () => { },

  // Layout
  onToggleLayout: () => { },
  onChangeLayout: () => { },

  // Contrast
  onToggleContrast: () => { },
  onChangeContrast: () => { },

  // Color
  onChangeColor: () => { },
  setColor: defaultPreset,
  colorOption: [],

  // Stretch
  onToggleStretch: () => { },

  // Reset
  onResetSetting: () => { },

  // Appointments View Mode
  onChangeAppointmentsViewMode: () => { },
};

const SettingsContext = createContext(initialState);

const SettingsProvider = ({ children }) => {
  const [settings, setSettings] = useState({
    themeMode: initialState.themeMode,
    themeLayout: initialState.themeLayout,
    themeStretch: initialState.themeStretch,
    themeContrast: initialState.themeContrast,
    themeDirection: initialState.themeDirection,
    themeColorPresets: initialState.themeColorPresets,
    sidebarOpen: false,
    appointmentsViewMode: initialState.appointmentsViewMode,
  });
  const [isLoading, setIsLoading] = useState(true);
  const socket = useSocket();

  const isArabic = localStorage.getItem("i18nextLng") === "ar";
  const isFirstRun = useRef(true);

  // Mode

  const onToggleMode = () => {
    setSettings((prev) => ({
      ...prev,
      themeMode: prev.themeMode === "light" ? "dark" : "light",
    }));
  };

  const onChangeMode = (event) => {
    const value = event.target.value;
    setSettings((prev) => ({
      ...prev,
      themeMode: value,
    }));
  };

  // Direction

  const onToggleDirection = () => {
    setSettings((prev) => ({
      ...prev,
      themeDirection: prev.themeDirection === "rtl" ? "ltr" : "rtl",
    }));
  };

  const onChangeDirection = (event) => {
    const value = event.target.value;
    setSettings((prev) => ({
      ...prev,
      themeDirection: value,
    }));
  };

  const onChangeDirectionByLang = (lang) => {
    setSettings((prev) => ({
      ...prev,
      themeDirection: lang === "ar" ? "rtl" : "ltr",
    }));
  };

  // Layout

  const onToggleLayout = () => {
    setSettings((prev) => ({
      ...prev,
      themeLayout:
        prev.themeLayout === "vertical" ? "horizontal" : "vertical",
    }));
  };

  const onChangeLayout = (event) => {
    const value = event.target.value;
    setSettings((prev) => ({
      ...prev,
      themeLayout: value,
    }));
  };

  // Contrast

  const onToggleContrast = () => {
    setSettings((prev) => ({
      ...prev,
      themeContrast: prev.themeContrast === "default" ? "bold" : "default",
    }));
  };

  const onChangeContrast = (event) => {
    const value = event.target.value;
    setSettings((prev) => ({
      ...prev,
      themeContrast: value,
    }));
  };

  // Color

  const onChangeColor = (event) => {
    const value = event.target.value;
    setSettings((prev) => ({
      ...prev,
      themeColorPresets: value,
    }));
  };

  // Stretch

  const onToggleStretch = () => {
    setSettings((prev) => ({
      ...prev,
      themeStretch: !prev.themeStretch,
    }));
  };

  const onToggleSidebar = () => {
    setSettings((prev) => ({
      ...prev,
      sidebarOpen: !prev.sidebarOpen,
    }));
  };

  // Appointments View Mode
  const onChangeAppointmentsViewMode = (event, newMode) => {
    if (newMode !== null) {
      setSettings((prev) => ({
        ...prev,
        appointmentsViewMode: newMode,
      }));
    }
  };

  // Reset

  const onResetSetting = () => {
    setSettings({
      themeMode: initialState.themeMode,
      themeLayout: initialState.themeLayout,
      themeStretch: initialState.themeStretch,
      themeContrast: initialState.themeContrast,
      themeDirection: initialState.themeDirection,
      themeColorPresets: initialState.themeColorPresets,
      sidebarOpen: false,
      appointmentsViewMode: initialState.appointmentsViewMode,
    });
  };

  useEffect(() => {
    if (isArabic) {
      onChangeDirectionByLang("ar");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isArabic]);

  // Fetch settings from backend on mount
  useEffect(() => {
    const fetchSettings = async () => {
      if (!localStorage.getItem("accessToken")) return;
      setIsLoading(true);
      try {
        const res = await getUserSettings();
        if (res.success && res.data) {
          // Ensure we don't overwrite with empty/null if DB is empty, but DB should have JSON
          // If res.data is empty object, we keep defaults.
          if (Object.keys(res.data).length > 0) {
            setSettings(prev => ({ ...prev, ...res.data }));
          }
        }
      } catch (error) {
        console.error("Failed to fetch settings", error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchSettings();
  }, []);

  const lastSocketSettings = useRef(null);

  // Save settings to backend on change
  useEffect(() => {
    if (isFirstRun.current) {
      isFirstRun.current = false;
      return;
    }

    // Check if this update is from a socket event
    if (lastSocketSettings.current === settings) {
      console.log("Skipping save for remote update");
      lastSocketSettings.current = null;
      return;
    }

    console.log("Settings changed, scheduling save:", settings);
    const timeoutId = setTimeout(() => {
      console.log("Saving settings to backend:", settings);
      updateUserSettings(settings).catch(err => console.error("Failed to save settings", err));
    }, 1000);
    return () => clearTimeout(timeoutId);
  }, [settings]);

  // Socket listener for settings updates
  useEffect(() => {
    if (!socket) return;

    const handleSettingsUpdate = (data) => {
      const currentUsername = localStorage.getItem("username");
      if (data.username === currentUsername) {
        console.log("Received settings update from socket:", data.settings);
        setSettings(prev => {
          const newSettings = { ...prev, ...data.settings };
          // Store the new state object reference to identify it in the effect
          lastSocketSettings.current = newSettings;
          return newSettings;
        });
      }
    };

    socket.on('settings_updated', handleSettingsUpdate);

    return () => {
      socket.off('settings_updated', handleSettingsUpdate);
    };
  }, [socket]);

  return (
    <SettingsContext.Provider
      value={{
        ...settings, // Mode
        onToggleMode,
        onChangeMode,

        // Direction
        onToggleDirection,
        onChangeDirection,
        onChangeDirectionByLang,

        // Layout
        onToggleLayout,
        onChangeLayout,

        // Contrast
        onChangeContrast,
        onToggleContrast,

        // Stretch
        onToggleStretch,

        // Color
        onChangeColor,
        setColor: getColorPresets(settings.themeColorPresets),
        colorOption: colorPresets.map((color) => ({
          name: color.name,
          value: color.main,
        })),

        // Reset
        onResetSetting,

        // Sidebar
        onToggleSidebar,

        // Appointments View Mode
        onChangeAppointmentsViewMode,

        // Loading State
        isLoading,
      }}
    >
      {children}
    </SettingsContext.Provider>
  );
};

export { SettingsContext };

export default SettingsProvider;